/**
 * Portal SignalR message dispatch — the live client / portal / role update side
 * of the real-time channel.
 *
 * Faithful port of the legacy `AppContainerComponent.portalMessageObservable`
 * subscription (`raptor-shell-ui/src/app/modules/app-container/app-container.component.ts`).
 * It reacts to the `portalupdatemethod` and `rumupdatemethod` hub messages by
 * driving the shell's existing legacy globals:
 *   - `EY_RAPToR_refreshClients(true)` — re-fetch the client dropdown,
 *   - `EY_RAPToR_PortalRefresh()`     — reload the mounted MFE (via a warning
 *      toast + delay, exactly like legacy `triggerMFERefresh`).
 *
 * The `portalincidentstatusmethod` message is intentionally NOT handled here —
 * the incident banner owns it (see `incidents-bridge.ts`). `dashboardupdatemethod`
 * is log-only in legacy, so it is a no-op here too.
 */

import { HubMethod, onPortalMessage, type PortalMessage } from '@/legacy/signal-r';
import { pushToast } from '@/ui/toast-store';
import { getTracker } from '@/telemetry/tracker';
import { envNumber } from '@/config/env-flags';

/** Legacy `signalRefreshDelayMs` (dev/qa = 2000). Env-driven for per-env parity. */
const REFRESH_DELAY_MS = envNumber(import.meta.env.VITE_SIGNAL_REFRESH_DELAY_MS, 2000);
/** Legacy `SignalR_Messages.Page_Refresh_Message`. */
const PAGE_REFRESH_MESSAGE = 'User settings changed, screen will refresh in few seconds.';

interface PortalUpdateResult {
  operation?: string;
  pScreens?: string[];
}

interface RumUpdateResult {
  operation?: string;
  method?: string;
  clientGuid?: string;
  clientGuidAdded?: string[];
}

let started = false;
let refreshTimer: number | undefined;

interface LegacyGlobals {
  EY_RAPToR_getCurrentClientId?: () => string;
  EY_RAPToR_refreshClients?: (force: boolean) => void;
  EY_RAPToR_PortalRefresh?: () => void;
}

function legacy(): LegacyGlobals {
  return window as unknown as LegacyGlobals;
}

function currentClientId(): string {
  try {
    return legacy().EY_RAPToR_getCurrentClientId?.() ?? '';
  } catch {
    return '';
  }
}

function refreshClients(): void {
  try {
    legacy().EY_RAPToR_refreshClients?.(true);
  } catch {
    /* best effort — the global may not be bound yet */
  }
}

/**
 * Warning toast, then after the delay reload via `EY_RAPToR_PortalRefresh`
 * (= `fullPortalRefresh`), which owns the cache-clear + refetch + loader +
 * success toast chain. Mirrors legacy `AppContainer.triggerMFERefresh`, which
 * only toasts + defers to `EY_RAPToR_PortalRefresh`.
 */
function triggerMfeRefresh(): void {
  pushToast({ variant: 'warning', message: PAGE_REFRESH_MESSAGE, timeoutMs: REFRESH_DELAY_MS });
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    try {
      legacy().EY_RAPToR_PortalRefresh?.();
    } catch {
      /* best effort */
    }
  }, REFRESH_DELAY_MS);
}

/** True when the updated screen affects what is currently mounted. */
function pathMatchesScreen(screen: string): boolean {
  // MainMenu / HeaderApps are global surfaces — always refresh (legacy parity).
  if (screen === 'MainMenu' || screen === 'HeaderApps') return true;
  // Mounted MFE URL is `/{clientId}/app-container/{appId}` (legacy parity).
  return window.location.pathname.indexOf(`/app-container/${screen}`) > -1;
}

function appTilePathMatches(screen: string): boolean {
  return window.location.pathname.endsWith(`/app-container/${screen}`);
}

function handlePortalUpdate(result: PortalUpdateResult | undefined): void {
  const operation = result?.operation;
  const screens = Array.isArray(result?.pScreens) ? result?.pScreens : [];

  if (
    operation === 'RoleScreenMappingUpdate' ||
    operation === 'ScreenUpdate' ||
    operation === 'RoleUpdate'
  ) {
    if (screens.length > 0) {
      if (screens.some(pathMatchesScreen)) triggerMfeRefresh();
    } else {
      triggerMfeRefresh();
    }
  } else if (operation === 'AppTileUpdate') {
    if (screens.length > 0) {
      if (screens.some(appTilePathMatches)) triggerMfeRefresh();
    } else {
      triggerMfeRefresh();
    }
  }
}

function handleRumUpdate(result: RumUpdateResult | undefined): void {
  if (!result) {
    triggerMfeRefresh();
    return;
  }

  if (result.operation === 'UserUpdate' && result.clientGuidAdded != null) {
    // A client was added to this user: if it's the active one, reload the MFE;
    // otherwise just refresh the dropdown so the new client appears.
    if (
      Array.isArray(result.clientGuidAdded) &&
      result.clientGuidAdded.includes(currentClientId())
    ) {
      triggerMfeRefresh();
    } else {
      refreshClients();
    }
  } else if (result.operation === 'ClientUpdate' && result.method === 'DisableClient') {
    // Tell other tabs the client was disabled (legacy `client-disabled` channel),
    // then re-fetch the dropdown so the disabled client drops off.
    try {
      const channel = new BroadcastChannel('client-disabled');
      channel.postMessage({ clientGuid: result.clientGuid, operation: 'DisableClient' });
      channel.close();
    } catch {
      /* BroadcastChannel unsupported — dropdown refresh below still applies */
    }
    refreshClients();
  } else if (result.operation === 'UserUpdate' && result.method === 'UserArchiveOrAdmin') {
    // Archive / admin-flag change flips the user's role set — refresh access too.
    triggerMfeRefresh();
  } else {
    refreshClients();
  }
}

/**
 * Subscribe the shell to portal/RUM SignalR updates. Idempotent. Safe to call
 * even before the connection opens — messages only flow once connected.
 */
export function startPortalMessageDispatch(): void {
  if (started) return;
  started = true;

  onPortalMessage((message: PortalMessage) => {
    switch (message.methodName) {
      case HubMethod.PortalUpdate:
        handlePortalUpdate(message.result as PortalUpdateResult | undefined);
        break;
      case HubMethod.RumUpdate:
        handleRumUpdate(message.result as RumUpdateResult | undefined);
        break;
      case HubMethod.DashboardUpdate:
        // Legacy logs only — no business logic.
        getTracker().event('signalr_dashboard_update');
        break;
      default:
        // PortalIncidentStatus is handled by incidents-bridge.ts.
        break;
    }
  });
}
